/**
 * Shop Mega Menu
 *
 * Desktop: full-screen category/product mega menu, opened by hovering (or
 * focusing) the header nav link whose label matches `data-trigger-label`
 * (see `blocks/_shop-mega-menu.liquid`).
 *
 * Mobile (<750px): the *same* panel and the *same* tab-block data, opened
 * instead by tapping the matching link inside the existing hamburger drawer
 * (`snippets/header-drawer.liquid`) — intercepted here rather than by
 * changing that shared snippet, which every other nav item still depends
 * on untouched. The panel itself becomes a drawer-width slide-in (CSS only,
 * see the block's stylesheet) sitting on top of the already-open drawer;
 * closing it (back arrow) reveals the drawer again, while its own "X"
 * button closes the drawer too via `<header-drawer>`'s public `close()`.
 *
 * Tab switching itself is handled entirely by the theme's existing generic
 * `<tabs-component>` (assets/tabs.js) via the standard `data-tab-trigger` /
 * `data-tab-panel` / `on:click="/selectTab?target=..."` contract — this
 * component only adds a debounced pointerenter-to-click bridge so tabs also
 * switch on hover on desktop (mobile already switches on tap, for free, via
 * that same declarative binding).
 */
import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, requestIdleCallback } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

const DEACTIVATE_DELAY = 350;
const HOVER_SWITCH_DELAY = 80;
// Matches the theme's `mobile:hidden` / `desktop:hidden` utility breakpoint
// (assets/base.css) that the desktop nav and the drawer already use, so this
// stays in sync with which trigger is actually visible/interactive.
const DESKTOP_QUERY = '(min-width: 750px)';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} backdrop
 * @property {HTMLElement} panelWrap
 *
 * @extends {Component<Refs>}
 */
class ShopMegaMenu extends Component {
  requiredRefs = ['backdrop', 'panelWrap'];

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #mobileTrigger = null;

  /** Whichever of #trigger/#mobileTrigger actually opened the menu — used to
   * reset aria-expanded and restore focus on the right element on close.
   * @type {HTMLElement | null} */
  #activeTrigger = null;

  /** @type {boolean} */
  #open = false;

  /** Set when a touch on a tablet opened the menu, so that same tap doesn't follow the link. */
  #openedByTap = false;

  /**
   * Set right before an Escape-triggered `trigger.focus()` call, so the
   * `focus` event that call itself fires doesn't immediately re-open the
   * menu through the same listener that opens it on real user focus.
   * @type {boolean}
   */
  #suppressNextFocusActivate = false;

  #abortController = new AbortController();

  /**
   * One per product slider, so each can be hidden independently once its
   * own content no longer overflows (see #setupArrowAutoHide). Desktop-only
   * concern — mobile's plain list never has arrows to begin with.
   * @type {ResizeObserver[]}
   */
  #arrowResizeObservers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#arrowResizeObservers.forEach((observer) => observer.disconnect());
    this.#arrowResizeObservers = [];
  }

  #setup() {
    const signal = this.#abortController.signal;

    this.#setupDesktopTrigger(signal);
    this.#setupMobileTrigger(signal);

    // Desktop-only in effect (both bail via #isDesktop()/#deactivateIfDesktop
    // below) — touch has no persistent hover, so a tap on anything inside
    // the panel fires a `pointerenter`+`pointerleave` pair of its own right
    // as the finger lifts. Without the guard, that `pointerleave` alone
    // would schedule this panel closing itself ~350ms after every tab tap,
    // regardless of breakpoint (hit concretely: tapping a tab appeared to
    // work, then the panel silently closed shortly after).
    this.refs.panelWrap.addEventListener('pointerenter', this.#activate, { signal });
    this.refs.panelWrap.addEventListener('pointerleave', this.#deactivateIfDesktop, { signal });
    this.refs.panelWrap.addEventListener('focusout', this.#handleBlur, { signal });
    // The backdrop is a click-to-dismiss surface, not part of the menu's own
    // content, even though it's a descendant of this element — so it gets an
    // explicit close handler rather than relying on #handleOutsideClick
    // (which treats anything inside `this` as "inside"). It's CSS-hidden on
    // mobile (the drawer's own backdrop does that job there), so this
    // shouldn't receive pointer events at all below 750px regardless — the
    // #isDesktop() guard is just defensive consistency with panelWrap above.
    this.refs.backdrop.addEventListener('pointerenter', this.#deactivateIfDesktop, { signal });
    this.refs.backdrop.addEventListener('click', this.#deactivate, { signal });

    // On `document`, not `this` — Escape should close the menu regardless of
    // which element currently has focus, not only when focus happens to be
    // inside the (possibly not-even-focusable) menu subtree.
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('pointerdown', this.#handleOutsideClick, { signal, capture: true });

    // Nothing above closes the panel on scroll — the pointer doesn't move relative to the
    // viewport as the page scrolls under it, so hover/pointerleave never fires. Left open, the
    // panel just stays floating on screen (positioned relative to the header) after the header
    // itself scrolls out of view or hides, then appears to "reattach" once a sticky header
    // reappears — so close it as soon as scrolling starts, same as Escape/outside-click.
    document.addEventListener('scroll', this.#deactivate, { signal, passive: true });

    this.#setupTabHoverSwitch(signal);
    this.#setupArrowAutoHide(this);
    this.addEventListener('tabpanelfill', this.#handlePanelFill, { signal });

    // Desktop opens on hover, so its first panel is filled ahead of time to open
    // instantly. Phones fill it on tap instead, in #show().
    onDocumentLoaded(() => {
      requestIdleCallback(() => {
        if (this.#isDesktop()) this.#fillActivePanel();
      });
    });
  }

  /** The panels ship in `<template>`s (see blocks/_shop-mega-menu-tab.liquid). */
  #fillActivePanel() {
    this.querySelector('tabs-component')?.fillActivePanel?.();
  }

  /** @param {CustomEvent<{ panel: HTMLElement }>} event */
  #handlePanelFill = (event) => {
    this.#setupArrowAutoHide(event.detail.panel);
  };

  /**
   * Desktop: finds the top-level nav link whose visible label matches the
   * configured trigger label, and wires hover/focus to open/close.
   * @param {AbortSignal} signal
   */
  #setupDesktopTrigger(signal) {
    const trigger = this.#findLabelMatch('.menu-list__list-item > .menu-list__link', '.menu-list__link-title');
    if (!trigger) return;

    this.#trigger = trigger;
    const item = trigger.closest('.menu-list__list-item') ?? trigger;

    item.addEventListener('pointerenter', this.#activate, { signal });
    item.addEventListener('pointerleave', this.#deactivateIfDesktop, { signal });
    trigger.addEventListener('focus', this.#activate, { signal });
    trigger.addEventListener('blur', this.#handleBlur, { signal });
    trigger.addEventListener('keydown', this.#handleTriggerKeydown, { signal });
    trigger.addEventListener('click', this.#handleTriggerClick, { signal });

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', this.refs.panelWrap.id);
    this.#addCaretIcon(trigger);
  }

  /**
   * Mobile: finds the matching plain link *inside the hamburger drawer*
   * (`.menu-drawer__menu-item--mainlist` — the class `header-drawer.liquid`
   * uses for a top-level link with no children, which "Shop" must have per
   * `blocks/_shop-mega-menu.liquid`'s own setup requirement) and intercepts
   * taps on it to open this panel instead of navigating away.
   * @param {AbortSignal} signal
   */
  #setupMobileTrigger(signal) {
    const trigger = this.#findLabelMatch('.menu-drawer__menu-item--mainlist', '.menu-drawer__menu-item-text');
    if (!trigger) return;

    this.#mobileTrigger = trigger;
    trigger.addEventListener('click', this.#activateMobile, { signal });
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    this.#addExpandIcon(trigger);
  }

  /**
   * Clones the "+" icon template (see blocks/_shop-mega-menu.liquid) into
   * the mobile trigger, so it visually matches the drawer's own accordion
   * items (which show one via `icon-plus.svg` next to their label) — "Shop"
   * has no children, so `header-drawer.liquid` never renders one for it on
   * its own.
   *
   * Also guards against something else re-rendering the drawer's HTML back
   * to its server-rendered state shortly after load and silently discarding
   * this — observed with `shopify theme dev`'s own hot-reload reconciling
   * sections post-load (the same class of thing already worked around for
   * `<tabs-component>`'s `[data-hydrated]` attribute). A short-lived
   * MutationObserver re-adds the icon if that happens, then disconnects
   * once the DOM has stayed stable for a few seconds.
   * @param {HTMLElement} trigger
   */
  #addExpandIcon(trigger) {
    const insert = () => {
      if (trigger.querySelector('.icon-plus')) return;

      const template = document.getElementById('shop-mega-menu-expand-icon');
      if (!(template instanceof HTMLTemplateElement)) return;

      trigger.appendChild(template.content.cloneNode(true));
    };

    insert();

    const observer = new MutationObserver(insert);
    observer.observe(trigger, { childList: true });
    setTimeout(() => observer.disconnect(), 5000);
  }

  /**
   * Clones the desktop dropdown caret template (see
   * blocks/_shop-mega-menu.liquid) into the desktop trigger, so it visually
   * matches the header menu's other top-level items that do have a
   * dropdown/mega-menu (`snippets/header-menu.liquid`'s own `icon-caret.svg`)
   * — "Shop" has no `link.links`, so that snippet never renders one for it.
   *
   * Same short-lived MutationObserver guard as `#addExpandIcon`, for the same
   * reason (hot-reload can reconcile the header back to its server-rendered
   * state shortly after load and silently discard this).
   * @param {HTMLElement} trigger
   */
  #addCaretIcon(trigger) {
    const insert = () => {
      if (trigger.querySelector('.menu-list__link-caret')) return;

      const template = document.getElementById('shop-mega-menu-caret-icon');
      if (!(template instanceof HTMLTemplateElement)) return;

      trigger.appendChild(template.content.cloneNode(true));
    };

    insert();

    const observer = new MutationObserver(insert);
    observer.observe(trigger, { childList: true });
    setTimeout(() => observer.disconnect(), 5000);
  }

  /**
   * Finds the first element matching `selector` whose visible label (via
   * `textSelector`, falling back to the element's own text) matches the
   * configured trigger label. Shared by the desktop and mobile trigger
   * lookups — they differ only in which part of the header they search.
   * @param {string} selector
   * @param {string} textSelector
   * @returns {HTMLElement | null}
   */
  #findLabelMatch(selector, textSelector) {
    const label = (this.dataset.triggerLabel || 'Shop').trim().toLowerCase();
    const candidates = document.querySelectorAll(selector);

    for (const el of candidates) {
      if (!(el instanceof HTMLElement)) continue;

      const text = (el.querySelector(textSelector)?.textContent ?? el.textContent ?? '').trim().toLowerCase();
      if (text !== label) continue;

      // Desktop only: skip a copy hidden inside the responsive overflow
      // ("More") bucket in favor of a currently-visible one, if any exists.
      const item = el.closest('.menu-list__list-item');
      if (item?.getAttribute('aria-hidden') === 'true') continue;

      return el;
    }

    return null;
  }

  /**
   * Hides each product slider's own arrow pair once its slides no longer
   * overflow (e.g. a tab with only 2-3 products on a wide screen) — there's
   * nothing to scroll to, so the arrows shouldn't show at all. Desktop-only:
   * mobile's list has no sliders to observe (querySelectorAll finds none).
   *
   * The theme's own `auto-hide-controls` slideshow-component attribute only
   * ever targets the *pagination dots* component (`slideshow-controls`,
   * which hard-codes `ref="slideshowControls"`) — it has no effect on plain
   * arrows, which is what every mega-menu tab uses here. A `ResizeObserver`
   * per scroller is the natural fit regardless: a tab's panel — and so its
   * carousel — starts at `display: none` (0 size) until that tab becomes
   * active, and a ResizeObserver fires again the moment it's actually
   * measurable, so this needs no separate hook into tab-switching or window
   * resize to stay correct.
   * @param {HTMLElement} root
   */
  #setupArrowAutoHide(root) {
    const sliders = root.querySelectorAll('.shop-mega-menu__slider');

    sliders.forEach((slider) => {
      const scroller = slider.querySelector('slideshow-slides');
      if (!(scroller instanceof HTMLElement)) return;

      const observer = new ResizeObserver(() => {
        const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1; // +1: subpixel rounding tolerance
        slider.classList.toggle('shop-mega-menu__slider--no-overflow', !hasOverflow);
      });

      observer.observe(scroller);
      this.#arrowResizeObservers.push(observer);
    });
  }

  /**
   * Bridges hover to the tab-switcher's own click handler so hovering a tab
   * trigger switches panels too, without duplicating `<tabs-component>`'s
   * switching logic. Desktop-only in effect — mobile has no `pointerenter`,
   * so this is simply inert there and mobile just uses the same triggers'
   * existing tap-to-switch binding directly.
   * @param {AbortSignal} signal
   */
  #setupTabHoverSwitch(signal) {
    const triggers = this.querySelectorAll('[data-tab-trigger]');

    triggers.forEach((tabTrigger) => {
      const switchToTab = debounce(() => {
        if (!(tabTrigger instanceof HTMLElement)) return;

        // A programmatic .click() on a button still moves focus to it like a
        // real click would — and browsers' :focus-visible heuristic treats
        // script-driven focus as keyboard-like, so without this it shows the
        // keyboard-only focus outline (blocks/_shop-mega-menu.liquid's
        // `:focus-visible` rule) on plain mouse hover. Calling `.blur()`
        // afterward to suppress that was tried and is actively dangerous
        // here: blurring an element inside the panel fires `focusout` on
        // `panelWrap` (bubbling), which #handleBlur reads as "focus left the
        // menu" and closes the whole thing — so hovering the very next tab
        // silently did nothing, because the panel had already closed under
        // it. Suppressing the outline via a class instead never touches
        // focus, so it can't trip that close-on-blur logic at all.
        // Left on (not removed on the next frame) until the button actually
        // loses focus — `:focus-visible` stays true for as long as focus
        // remains, so removing the class any earlier would just let the
        // outline reappear while the button is still focused.
        tabTrigger.classList.add('shop-mega-menu__tab-trigger--suppress-outline');
        tabTrigger.addEventListener(
          'blur',
          () => tabTrigger.classList.remove('shop-mega-menu__tab-trigger--suppress-outline'),
          { once: true }
        );
        tabTrigger.click();
      }, HOVER_SWITCH_DELAY);

      tabTrigger.addEventListener('pointerenter', switchToTab, { signal });
      tabTrigger.addEventListener('pointerleave', () => switchToTab.cancel(), { signal });
    });
  }

  #isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  /** Desktop hover/focus handler. */
  #activate = () => {
    if (this.#suppressNextFocusActivate) {
      this.#suppressNextFocusActivate = false;
      return;
    }

    if (!this.#isDesktop() || this.#open) {
      this.#debouncedDeactivate.cancel();
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#show(this.#trigger);
    this.#openedByTap = window.matchMedia('(hover: none)').matches;
  };

  /**
   * The panel sits outside the nav in the DOM, so Tab alone can't reach it from the trigger.
   * @param {KeyboardEvent} event
   */
  #handleTriggerKeydown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== ' ') return;

    event.preventDefault();
    if (!this.#open) this.#show(this.#trigger);
    this.querySelector('[data-tab-trigger][tabindex="0"]')?.focus();
  };

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    if (this.#openedByTap) event.preventDefault();
    this.#openedByTap = false;
  };

  /**
   * Mobile tap handler — opens the same panel the desktop hover path does,
   * instead of letting the drawer link navigate away.
   * @param {MouseEvent} event
   */
  #activateMobile = (event) => {
    if (this.#isDesktop() || this.#open) return;

    event.preventDefault();
    this.#show(this.#mobileTrigger);
  };

  /** @param {HTMLElement | null} trigger */
  #show(trigger) {
    this.#fillActivePanel();
    this.#open = true;
    this.dataset.open = 'true';
    this.#activeTrigger = trigger;
    trigger?.setAttribute('aria-expanded', 'true');

    if (trigger === this.#mobileTrigger) {
      // The drawer's own trapFocus() (assets/focus.js, called by
      // header-drawer.js when it opens) is a *global*, document-level
      // focusin trap: it redirects any focus landing outside the drawer's
      // own <details> back to the drawer's first focusable element (the
      // hamburger toggle) — including focus tabs.js sets on a newly-active
      // tab trigger inside *this* panel, since this panel deliberately
      // lives outside that <details> (see the module doc comment above).
      // That redirect then reads as "focus left the menu" to #handleBlur
      // and closes this panel right back up — concretely: tapping any tab
      // silently closed the whole panel, focus landing on the hamburger
      // instead of the tapped tab. Suspending the drawer's trap while this
      // panel owns the interaction avoids that; #deactivate() below
      // restores it once the drawer is the primary surface again.
      removeTrapFocus();
    }
  }

  #deactivate = () => {
    if (!this.#open) return;

    const wasMobile = this.#activeTrigger === this.#mobileTrigger;

    this.#open = false;
    delete this.dataset.open;
    this.#activeTrigger?.setAttribute('aria-expanded', 'false');
    this.#activeTrigger = null;

    if (wasMobile) {
      const drawer = document.querySelector('.menu-drawer-container');
      if (drawer instanceof HTMLElement && drawer.hasAttribute('open')) {
        trapFocus(drawer);
      }
    }
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Wraps #debouncedDeactivate for the pointer-hover listeners specifically
   * (nav item/panelWrap/backdrop pointer events) — those model desktop "mouse
   * moved away" intent, which doesn't exist on touch (see the connectedCallback
   * comment above panelWrap's pointerleave binding). The pointer type check
   * covers touch tablets, which are wide enough to count as desktop.
   * @param {PointerEvent} event
   */
  #deactivateIfDesktop = (event) => {
    if (this.#isDesktop() && event.pointerType !== 'touch') this.#debouncedDeactivate();
  };

  /**
   * Closes just this panel — the mobile "back" control, and the public
   * method Escape/outside-click already route through. On mobile this
   * reveals the still-open drawer behind it; on desktop it's the same as
   * any other close.
   */
  close() {
    this.#deactivate();
  }

  /**
   * Mobile "X" control: closes this panel *and* the hamburger drawer it's
   * sitting on top of, since — unlike the back arrow — the intent here is
   * to leave the menu entirely, not step back into it.
   */
  closeAll() {
    this.#deactivate();
    document.querySelector('header-drawer')?.close();
  }

  /** @param {FocusEvent} event */
  #handleBlur = (event) => {
    const nextFocus = event.relatedTarget;
    // Defer so focus has actually landed before checking whether it's still inside the menu or its trigger.
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement === this.#activeTrigger || this.contains(activeElement) || this.contains(nextFocus)) return;
      this.#deactivate();
    }, 0);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#open) return;

    const trigger = this.#activeTrigger;
    this.#deactivate();
    this.#suppressNextFocusActivate = true;
    trigger?.focus();
  };

  /** @param {PointerEvent} event */
  #handleOutsideClick = (event) => {
    if (!this.#open) return;

    const target = event.target;
    if (
      target instanceof Node &&
      (this.contains(target) || this.#trigger?.contains(target) || this.#mobileTrigger?.contains(target))
    ) {
      return;
    }

    // On mobile this also covers the drawer's own backdrop/X/Escape closing
    // the drawer out from under this panel — any of those land somewhere
    // outside `this`, so this panel closes right along with it.
    this.#deactivate();
  };
}

if (!customElements.get('shop-mega-menu')) {
  customElements.define('shop-mega-menu', ShopMegaMenu);
}
